import { supabase } from "@/integrations/supabase/client";

/**
 * Helper to invoke admin-protected edge functions with proper authorization.
 * Automatically includes the user's auth token in the request.
 */
export async function invokeAdminFunction<T = unknown>(
  functionName: string,
  body?: Record<string, unknown>
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      return { 
        data: null, 
        error: new Error('Not authenticated. Please log in as an admin.') 
      };
    }

    const { data, error } = await supabase.functions.invoke(functionName, {
      body,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      /**
       * supabase-js distinguishes two failure shapes, and we were throwing away
       * the difference — every one surfaced as the same opaque
       * "Failed to send a request to the Edge Function".
       *
       *   FunctionsHttpError  — the request reached the function and it answered
       *                         non-2xx. error.context is the Response, so the
       *                         real status (404 not deployed / 500 crashed /
       *                         401 auth) is sitting right there.
       *   FunctionsFetchError — fetch itself rejected: no HTTP response at all.
       *                         CORS preflight or network, NOT a missing
       *                         function (a 404 would resolve and be HttpError).
       *   FunctionsRelayError — the relay failed.
       *
       * Reading and reporting these turns "something went wrong" into a
       * diagnosis, which matters because several admin jobs are failing this way
       * and the status is the one fact that separates the causes.
       */
      const name = (error as { name?: string }).name;
      const ctx = (error as { context?: unknown }).context;

      if (name === 'FunctionsHttpError' && ctx instanceof Response) {
        const status = ctx.status;
        let detail = '';
        try {
          const text = await ctx.clone().text();
          if (text) detail = ` — ${text.slice(0, 300)}`;
        } catch {
          /* body already consumed or unreadable; the status is the useful part */
        }
        if (status === 401) {
          return { data: null, error: new Error(`401 Unauthorized — log in again.${detail}`) };
        }
        if (status === 403) {
          return { data: null, error: new Error(`403 Forbidden — admin access required.${detail}`) };
        }
        if (status === 404) {
          return {
            data: null,
            error: new Error(`404 Not Found — "${functionName}" is not deployed.${detail}`),
          };
        }
        return { data: null, error: new Error(`HTTP ${status} from "${functionName}"${detail}`) };
      }

      if (name === 'FunctionsFetchError') {
        return {
          data: null,
          error: new Error(
            `Could not reach "${functionName}" — no HTTP response (CORS preflight or network). ` +
            `Note this is NOT a missing function: a 404 would have returned a status.`
          ),
        };
      }

      return { data: null, error: new Error(`${name || 'Error'} from "${functionName}": ${error.message}`) };
    }

    return { data: data as T, error: null };
  } catch (err) {
    return { 
      data: null, 
      error: err instanceof Error ? err : new Error('Unknown error occurred') 
    };
  }
}
